/* Batch 11 · A1 — "Review levels" on the engagement (Partner / Manager): Reviewer 2 and Reviewer 3 added or removed at any
   stage (removing a level that holds sign-offs asks to confirm; those sign-offs stay in the timeline), and the engagement
   partner who signs the Partner level. The EQ reviewer level is not set here — it shows when the engagement requires an EQ review. */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)

export default function ReviewLevelsDialog({ eng, open, onOpenChange }) {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ reviewer2: false, reviewer3: false, partner: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!(open && eng)) return;
    setD(null);
    api.get(`/engagements/${eng.id}/review-levels`).then((r) => {
      setD(r.data);
      setForm({ reviewer2: !!r.data.review_levels.reviewer2, reviewer3: !!r.data.review_levels.reviewer3,
                partner: r.data.engagement_partner_user_id || "" });
    }).catch((e) => toast.error(toErrMsg(e, t("itemSignoff.failed"))));
  }, [open, eng, t]);

  const save = async (confirm = false) => {
    setBusy(true);
    try {
      await api.put(`/engagements/${eng.id}/review-levels`, { reviewer2: form.reviewer2, reviewer3: form.reviewer3,
        engagement_partner_user_id: form.partner, confirm });
      toast.success(t("itemSignoff.saved"));
      onOpenChange(false);
    } catch (e) {
      const code = e?.response?.data?.error_code;
      if (code === "SIGNOFF_LEVEL_HAS_SIGNOFFS" && !confirm) {
        if (await askConfirm(`${t("itemSignoff.confirmRemoveLevel")}\n\n${e.response.data.detail || ""}`)) { setBusy(false); return save(true); }
      } else {
        toast.error(toErrMsg(e, t("itemSignoff.failed")));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="review-levels-dialog">
        <DialogHeader>
          <DialogTitle>{t("itemSignoff.reviewLevels")}</DialogTitle>
          <DialogDescription>{t("itemSignoff.reviewLevelsHelp")}</DialogDescription>
        </DialogHeader>
        {!d ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div> : (
          <div className="space-y-3 text-sm">
            <div className="text-xs text-muted-foreground" data-testid="review-levels-active">
              {d.active_levels.map((lv) => t(`itemSignoff.level.${lv}`)).join(" · ")}
            </div>
            {["reviewer2", "reviewer3"].map((lv) => (
              <label key={lv} className="flex items-center gap-2">
                <Checkbox checked={form[lv]} disabled={!d.can_edit} onCheckedChange={(v) => setForm({ ...form, [lv]: !!v })}
                          data-testid={`review-levels-${lv}`} />
                {t(`itemSignoff.level.${lv}`)}{d.held?.[lv] ? <span className="text-xs text-muted-foreground"> ({d.held[lv]})</span> : null}
              </label>
            ))}
            <label className="block text-xs">{t("itemSignoff.engagementPartner")}
              <select className="mt-1 w-full rounded border px-2 py-1" value={form.partner} disabled={!d.can_edit}
                      onChange={(e) => setForm({ ...form, partner: e.target.value })} data-testid="review-levels-partner">
                <option value="">{t("itemSignoff.anyPartner")}</option>
                {asArray(d.partners).map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
              </select>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button disabled={busy || !d?.can_edit} onClick={() => save(false)} data-testid="review-levels-save">{t("itemSignoff.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
